/**
 * Persona detection — the single source of truth for
 * visitor → persona classification. Used across:
 *   • Landing hero headline
 *   • Skill-check persona-selector step
 *   • Skill-check result screen headline + CTA
 *
 * Detection order (highest confidence first):
 *   1. Explicit user choice in sessionStorage `skill_check_persona`
 *      — set by the persona selector step. Trumps everything else.
 *   2. UTM source (utm_source) — the marketing tag on the landing URL.
 *   3. Document referrer — inbound domain from ad clicks / cold-email
 *      links / course platforms.
 *   4. Fallback → 'professional'.
 *
 * Persona values used everywhere: 'sales' | 'student' | 'builder' | 'professional'
 * `professional` is the safe default and maps to the existing (writing /
 * general knowledge-worker) content.
 */

const STORAGE_KEY = "skill_check_persona";

const SALES_UTM_SOURCES = new Set([
  "linkedin", "linked-in", "outreach", "sales-nav", "salesnavigator",
  "apollo", "mailshake", "instantly", "smartlead", "lemlist", "cold-email",
  "coldemail", "hubspot",
]);

const STUDENT_UTM_SOURCES = new Set([
  "canvas", "moodle", "blackboard", "coursera", "chegg", "quizlet",
  "khanacademy", "duolingo", "gradescope", "student",
]);

const BUILDER_UTM_SOURCES = new Set([
  "github", "stackoverflow", "hackernews", "hn", "producthunt", "dev.to",
  "devto", "reddit-programming", "vercel", "netlify",
]);

const SALES_REFERRER_HOSTS = [
  "linkedin.com", "sales-navigator", "hubspot.com", "salesloft.com",
  "outreach.io", "apollo.io", "mailshake.com", "instantly.ai", "lemlist.com",
  "smartlead.ai",
];

const STUDENT_REFERRER_PATTERNS = [
  ".edu", ".edu.", "canvas.", "moodle.", "blackboard.", "coursera.",
  "chegg.com", "quizlet.com", "khanacademy.org", "gradescope.com",
];

const BUILDER_REFERRER_HOSTS = [
  "github.com", "stackoverflow.com", "news.ycombinator.com",
  "producthunt.com", "dev.to", "hashnode.com", "reddit.com/r/programming",
];


function _readUrlParams() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: (params.get("utm_source") || "").toLowerCase().trim(),
      utm_medium: (params.get("utm_medium") || "").toLowerCase().trim(),
      utm_campaign: (params.get("utm_campaign") || "").toLowerCase().trim(),
    };
  } catch (_e) { return {}; }
}

function _readReferrer() {
  if (typeof document === "undefined") return "";
  try { return (document.referrer || "").toLowerCase(); } catch (_e) { return ""; }
}


/**
 * Return the current visitor's persona classification.
 * Non-destructive — never writes to storage. Call `setPersona()` to persist.
 */
export function detectPersona() {
  // 1. Explicit choice wins.
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && ["sales", "student", "builder", "professional"].includes(stored)) {
      return stored;
    }
  } catch (_e) { /* private mode / SSR */ }

  const { utm_source, utm_medium } = _readUrlParams();
  const referrer = _readReferrer();

  // 2. UTM source is the highest-confidence tag.
  if (utm_source) {
    if (SALES_UTM_SOURCES.has(utm_source)) return "sales";
    if (STUDENT_UTM_SOURCES.has(utm_source)) return "student";
    if (BUILDER_UTM_SOURCES.has(utm_source)) return "builder";
  }
  if (utm_medium === "email" && utm_source && utm_source.includes("cold")) return "sales";

  // 3. Referrer host classification.
  if (referrer) {
    if (SALES_REFERRER_HOSTS.some((h) => referrer.includes(h))) return "sales";
    if (STUDENT_REFERRER_PATTERNS.some((p) => referrer.includes(p))) return "student";
    if (BUILDER_REFERRER_HOSTS.some((h) => referrer.includes(h))) return "builder";
  }

  return "professional";
}


/** Persist an explicit persona choice — called by the selector step. */
export function setPersona(persona) {
  if (!persona) return;
  const ok = ["sales", "student", "builder", "professional"];
  if (!ok.includes(persona)) return;
  try { sessionStorage.setItem(STORAGE_KEY, persona); } catch (_e) { /* ignore */ }
}


/** Read the stored persona choice, if any. */
export function getStoredPersona() {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch (_e) { return null; }
}


/**
 * Copy variants for the 3 surfaces.
 * Kept in one file so the copy edits stay atomic — one PR = one file.
 */
export const PERSONA_COPY = {
  hero_headline: {
    sales:        "Write Cold Emails That Get Replies — Take the AI Sales Skill Check",
    student:      "Use AI to Finish Your Essay 2x Faster — Start Free",
    builder:      "Score Your AI Skills in 90 Seconds — See Where You're Wasting Time",
    professional: "Score Your AI Skills in 90 Seconds — See Where You're Wasting Time",
  },
  result_headline: {
    sales:        "Here's How to Close More Deals With AI",
    student:      "Here's How to Finish Homework Faster",
    builder:      "Here's How You Can Ship 30% More Code Tomorrow",
    professional: "Here's How You Can Save 30+ Minutes Tomorrow",
  },
  result_cta: {
    sales:        "Write My First High-Reply Cold Email (3 min)",
    student:      "Try Your First Real Lesson: Explain Hard Concepts Simply (1 min)",
    builder:      "Try Your First Real Lesson: Ship a Cleaner JSON Schema (2 min)",
    professional: "Start Your First Win: Write a Better Email Prompt (2 min)",
  },
  scenario_prompt: {
    sales:        "Write a prompt that generates a personalized cold email for a SaaS buyer.",
    student:      "Write a prompt to explain a complex concept from your textbook.",
    builder:      "Write a prompt that generates a JSON schema for a user profile API.",
    professional: null,  // null → keep existing randomized scenario for the default "writing" path
  },
  selector_label: {
    sales:        "Sales & Outreach",
    student:      "School & Research",
    builder:      "Building & Code",
    professional: "Writing & Content",
  },
};
